import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Copy, Trash2, Pencil, X, Search } from "lucide-react";
import { adminApi, emptyAd, AD_TYPES } from "@/lib/adminApi";
import { AdForm } from "@/admin/AdForm";

const typeLabel = (v) => (AD_TYPES.find((t) => t[0] === v) || [v, v])[1];

export const AdsManager = () => {
  const [ads, setAds] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const [editing, setEditing] = useState(null); // ad object or null
  const [isNew, setIsNew] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/ads", { params: { q } });
      setAds(r.data.ads);
    } catch { toast.error("Could not load ads"); } finally { setLoading(false); }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const toggleSel = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Name is required");
    if (editing.placements.length === 0) return toast.error("Pick at least one placement");
    try {
      if (isNew) await adminApi.post("/admin/ads", editing);
      else await adminApi.put(`/admin/ads/${editing.id}`, editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const toggleEnabled = async (ad) => {
    await adminApi.put(`/admin/ads/${ad.id}`, { ...ad, enabled: !ad.enabled });
    load();
  };

  const duplicate = async (id) => { await adminApi.post(`/admin/ads/${id}/duplicate`); toast.success("Duplicated"); load(); };
  const remove = async (id) => { if (!window.confirm("Delete this ad?")) return; await adminApi.delete(`/admin/ads/${id}`); load(); };

  const bulk = async (action) => {
    if (sel.length === 0) return;
    if (action === "delete" && !window.confirm(`Delete ${sel.length} ads?`)) return;
    await adminApi.post("/admin/ads/bulk", { ids: sel, action });
    toast.success(`${action} applied to ${sel.length}`); setSel([]); load();
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex flex-1 items-center rounded-lg border border-neutral-300 bg-white px-3">
          <Search className="h-4 w-4 text-neutral-400" />
          <input data-testid="ads-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ads…" className="w-full px-2 py-2 text-sm outline-none" />
        </div>
        {sel.length > 0 && (
          <div className="flex gap-2">
            <button data-testid="ads-bulk-enable" onClick={() => bulk("enable")} className="rounded-lg border border-neutral-300 px-3 py-2 text-xs font-semibold">Enable ({sel.length})</button>
            <button data-testid="ads-bulk-disable" onClick={() => bulk("disable")} className="rounded-lg border border-neutral-300 px-3 py-2 text-xs font-semibold">Disable</button>
            <button data-testid="ads-bulk-delete" onClick={() => bulk("delete")} className="rounded-lg border border-red-300 px-3 py-2 text-xs font-semibold text-red-600">Delete</button>
          </div>
        )}
        <button data-testid="ads-new" onClick={() => { setEditing(emptyAd()); setIsNew(true); }} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700">
          <Plus className="h-4 w-4" /> New Ad
        </button>
      </div>

      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase text-neutral-500">
            <tr>
              <th className="w-8 px-3 py-2.5"></th>
              <th className="px-3 py-2.5">Name</th>
              <th className="px-3 py-2.5">Type</th>
              <th className="px-3 py-2.5">Placements</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5">Imp / Clk</th>
              <th className="px-3 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-neutral-400">Loading…</td></tr>
            ) : ads.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-neutral-400">No ads yet. Click “New Ad” to create your first placement.</td></tr>
            ) : ads.map((ad) => (
              <tr key={ad.id} data-testid={`ad-row-${ad.id}`} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50">
                <td className="px-3 py-2.5"><input type="checkbox" checked={sel.includes(ad.id)} onChange={() => toggleSel(ad.id)} /></td>
                <td className="px-3 py-2.5 font-medium">{ad.name}</td>
                <td className="px-3 py-2.5 text-neutral-500">{typeLabel(ad.type)}</td>
                <td className="px-3 py-2.5 text-xs text-neutral-500">{ad.placements.join(", ") || "—"}</td>
                <td className="px-3 py-2.5">
                  <button data-testid={`ad-toggle-${ad.id}`} onClick={() => toggleEnabled(ad)} className={`rounded-full px-2 py-0.5 text-xs font-bold ${ad.enabled && ad.status === "active" ? "bg-green-100 text-green-700" : "bg-neutral-200 text-neutral-500"}`}>
                    {ad.enabled ? (ad.status === "active" ? "Live" : ad.status) : "Off"}
                  </button>
                </td>
                <td className="px-3 py-2.5 text-xs text-neutral-500">{ad.impressions || 0} / {ad.clicks || 0}</td>
                <td className="px-3 py-2.5">
                  <div className="flex justify-end gap-1.5">
                    <button data-testid={`ad-edit-${ad.id}`} onClick={() => { setEditing(ad); setIsNew(false); }} className="rounded p-1.5 hover:bg-neutral-200"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => duplicate(ad.id)} className="rounded p-1.5 hover:bg-neutral-200"><Copy className="h-4 w-4" /></button>
                    <button onClick={() => remove(ad.id)} className="rounded p-1.5 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-black/50 p-4">
          <div className="my-8 w-full max-w-4xl rounded-2xl bg-white p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold">{isNew ? "New Advertisement" : "Edit Advertisement"}</h3>
              <button onClick={() => setEditing(null)}><X className="h-5 w-5" /></button>
            </div>
            <AdForm ad={editing} setAd={setEditing} />
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-semibold">Cancel</button>
              <button data-testid="adform-save" onClick={save} className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-bold text-white hover:bg-blue-700">Save Ad</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
